import React, { useState, useEffect, useRef } from 'react';
import { Image as ImageIcon } from 'lucide-react';
import api from '../../../../../services/api';
import { uploadImage } from '../../../services/rfpCoreService';

const ImageDropdown = ({ editor, editorRef, rfpId }) => {
    const [isOpen, setIsOpen] = useState(false);
    const [activeTab, setActiveTab] = useState('upload');
    const [imageUrl, setImageUrl] = useState('');
    const [isUploading, setIsUploading] = useState(false);
    const [uploadError, setUploadError] = useState(null);
    const [uploadSuccess, setUploadSuccess] = useState(false);
    const dropdownRef = useRef(null);
    const fileInputRef = useRef(null);

    const activeEditor = editorRef?.current || editor;

    useEffect(() => {
        const handleClickOutside = (event) => {
            if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
                setIsOpen(false);
                setUploadError(null);
                setUploadSuccess(false);
            }
        };
        document.addEventListener('mousedown', handleClickOutside);
        return () => document.removeEventListener('mousedown', handleClickOutside);
    }, []);

    const validateImageUrl = (urlStr) => {
        const trimmed = urlStr.trim();
        // Allow base64 data URIs
        if (trimmed.startsWith('data:image/')) return true;

        try {
            const url = new URL(trimmed);
            const hostname = url.hostname.toLowerCase();
            
            // Reject search engine image search result pages
            if (hostname.includes('bing.com') && (url.pathname.includes('images') || url.search.includes('q='))) return false;
            if (hostname.includes('google.com') && (url.pathname.includes('imgres') || url.search.includes('q='))) return false;
            
            const pathname = url.pathname.toLowerCase();
            const allowedExtensions = ['.png', '.jpg', '.jpeg', '.webp', '.svg', '.gif'];
            
            // Must contain or end with a valid image extension
            const hasValidExtension = allowedExtensions.some(ext => pathname.includes(ext));
            if (!hasValidExtension) {
                // Fallback for valid http/s URLs that don't have file extensions
                if (trimmed.startsWith('http://') || trimmed.startsWith('https://')) return true;
                return false;
            }
            
            return true;
        } catch (e) {
            return false;
        }
    };
 
    const handleInsertUrl = async () => {
        const trimmedUrl = imageUrl.trim();
        if (!trimmedUrl || !activeEditor) return;
 
        // 1. Validate direct image URL or base64 URI
        if (!validateImageUrl(trimmedUrl)) {
            setUploadError("Please provide a direct image URL.");
            return;
        }
 
        setIsUploading(true);
        setUploadError(null);
        setUploadSuccess(false);

        // 2. Direct Base64 Data URI insertion bypasses external fetch/upload
        if (trimmedUrl.startsWith('data:image/')) {
            try {
                activeEditor.chain().focus().setImage({ 
                    src: trimmedUrl, 
                    alt: 'Embedded Image',
                    'data-width-percent': '50', // Default width is 50%
                    'data-align': 'center'
                }).run();
                setUploadSuccess(true);
                setTimeout(() => { setIsOpen(false); setUploadSuccess(false); }, 1000);
            } catch (e) {
                setUploadError("Failed to insert base64 image.");
            } finally {
                setIsUploading(false);
                setImageUrl('');
            }
            return;
        }
 
        try {
            // 3. Best Practice: Fetch the external image as a blob and upload to AWS S3
            // This ensures long-term reliability and ownership of the image asset.
            const response = await fetch(trimmedUrl, { mode: 'cors' });
            if (!response.ok) throw new Error("External fetch failed");
 
            const blob = await response.blob();
            const urlObj = new URL(trimmedUrl);
            const ext = urlObj.pathname.split('.').pop()?.toLowerCase() || 'png';
            const file = new File([blob], `imported-image.${ext}`, { type: blob.type || `image/${ext}` });
 
            const formData = new FormData();
            formData.append('file', file);
 
            const uploadResponse = await uploadImage(rfpId, formData);
 
            const linkUrl = uploadResponse.data?.url || uploadResponse.data?.link;
            if (linkUrl) {
                activeEditor.chain().focus().setImage({ 
                    src: linkUrl, 
                    alt: 'Embedded Image',
                    'data-width-percent': '50', // Default width is 50%
                    'data-align': 'center'
                }).run();
                setUploadSuccess(true);
                setTimeout(() => { setIsOpen(false); setUploadSuccess(false); }, 1000);
            } else {
                throw new Error("No URL returned from S3 upload");
            }
        } catch (error) {
            console.warn("CORS or network error uploading external image, falling back to direct URL insertion:", error);
            // 4. Graceful Fallback: Insert direct external image URL
            activeEditor.chain().focus().setImage({ 
                src: trimmedUrl, 
                alt: 'Embedded Image',
                'data-width-percent': '50', // Default width is 50%
                'data-align': 'center'
            }).run();
            setUploadSuccess(true);
            setTimeout(() => { setIsOpen(false); setUploadSuccess(false); }, 1000);
        } finally {
            setIsUploading(false);
            setImageUrl('');
        }
    };

    const handleFileChange = async (e) => {
        const file = e.target.files?.[0];
        if (!file) return;

        const allowedTypes = ['image/jpeg', 'image/jpg', 'image/png', 'image/webp'];
        if (!allowedTypes.includes(file.type)) {
            setUploadError('Format must be JPG, JPEG, PNG, or WEBP.');
            return;
        }
        if (file.size > 5 * 1024 * 1024) {
            setUploadError('Image size must be less than 5MB.');
            return;
        }

        setIsUploading(true);
        setUploadError(null);
        setUploadSuccess(false);

        const formData = new FormData();
        formData.append('file', file);

        try {
            if (!activeEditor) throw new Error('Editor is not initialized yet.');
            const response = await uploadImage(rfpId, formData);
            const linkUrl = response.data?.url || response.data?.link;
            if (linkUrl) {
                activeEditor.chain().focus().setImage({ 
                    src: linkUrl, 
                    alt: file.name,
                    'data-width-percent': '50',
                    'data-align': 'center'
                }).run();
                setUploadSuccess(true);
                setTimeout(() => { setIsOpen(false); setUploadSuccess(false); }, 1000);
            } else {
                throw new Error('No URL returned from backend');
            }
        } catch (error) {
            console.error('Server upload failed:', error);
            setUploadError('Server upload failed. Base64 uploads are disabled.');
        } finally {
            setIsUploading(false);
        }
    };

    return (
        <div className="list-style-dropdown-container" ref={dropdownRef}>
            <button
                className={`toolbar-btn ${isOpen ? 'active' : ''}`}
                onClick={() => setIsOpen(!isOpen)}
                title="Insert Image"
            >
                <ImageIcon size={16} />
                <span className="toolbar-tooltip">Insert Image</span>
            </button>
            {isOpen && (
                <div className="library-dropdown-menu image-toolbar-dropdown" style={{ width: '280px', padding: '16px', left: 'auto', right: 0 }}>
                    <div style={{ display: 'flex', borderBottom: '1px solid #E2E8F0', marginBottom: '12px', gap: '12px' }}>
                        <button
                            style={{
                                border: 'none', background: 'none', paddingBottom: '8px', cursor: 'pointer',
                                fontSize: '13px', fontWeight: '600',
                                color: activeTab === 'upload' ? '#FF5526' : '#64748B',
                                borderBottom: activeTab === 'upload' ? '2px solid #FF5526' : '2px solid transparent'
                            }}
                            onClick={() => { setActiveTab('upload'); setUploadError(null); }}
                        >
                            Upload File
                        </button>
                        <button
                            style={{
                                border: 'none', background: 'none', paddingBottom: '8px', cursor: 'pointer',
                                fontSize: '13px', fontWeight: '600',
                                color: activeTab === 'url' ? '#FF5526' : '#64748B',
                                borderBottom: activeTab === 'url' ? '2px solid #FF5526' : '2px solid transparent'
                            }}
                            onClick={() => { setActiveTab('url'); setUploadError(null); }}
                        >
                            Image URL
                        </button>
                    </div>
                    {activeTab === 'upload' ? (
                        <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center' }}>
                            <input
                                type="file"
                                ref={fileInputRef}
                                style={{ display: 'none' }}
                                accept="image/*"
                                onChange={handleFileChange}
                            />
                            <div
                                style={{
                                    border: '2px dashed #CBD5E1', borderRadius: '8px', padding: '20px 12px',
                                    width: '100%', display: 'flex', flexDirection: 'column', alignItems: 'center',
                                    cursor: 'pointer', transition: 'all 0.2s', background: '#F8F9FA'
                                }}
                                onClick={() => !isUploading && fileInputRef.current?.click()}
                                onMouseEnter={(e) => e.currentTarget.style.borderColor = '#FF5526'}
                                onMouseLeave={(e) => e.currentTarget.style.borderColor = '#CBD5E1'}
                            >
                                <ImageIcon size={28} style={{ color: '#94A3B8', marginBottom: '8px' }} />
                                <span style={{ fontSize: '12px', fontWeight: '500', color: '#64748B', textAlign: 'center' }}>
                                    {isUploading ? 'Uploading...' : uploadSuccess ? 'Success!' : 'Click to choose image file'}
                                </span>
                            </div>
                            {uploadError && (
                                <div style={{ fontSize: '11px', color: '#EF4444', marginTop: '8px', textAlign: 'center' }}>
                                    {uploadError}
                                </div>
                            )}
                        </div>
                    ) : (
                        <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
                            <input
                                type="text"
                                placeholder="Paste image absolute URL..."
                                value={imageUrl || ''}
                                onChange={(e) => setImageUrl(e.target.value)}
                                style={{
                                    width: '100%', padding: '8px 12px', border: '1px solid #E2E8F0',
                                    borderRadius: '6px', fontSize: '13px', outline: 'none'
                                }}
                            />
                            <button
                                onClick={handleInsertUrl}
                                disabled={!imageUrl.trim()}
                                style={{
                                    background: imageUrl.trim() ? '#FF5526' : '#CBD5E1',
                                    color: '#FFFFFF', border: 'none', padding: '8px',
                                    borderRadius: '6px', fontSize: '13px', fontWeight: '600',
                                    cursor: imageUrl.trim() ? 'pointer' : 'not-allowed', transition: 'all 0.2s'
                                }}
                            >
                                Insert Image
                            </button>
                        </div>
                    )}
                </div>
            )}
        </div>
    );
};

export default ImageDropdown;
